import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const SiteSettingsContext = createContext(null);

/**
 * Fetches /api/settings once and exposes it via context. Also injects
 * tracking codes (GA4, GTM, Google Search Console verification, Meta Pixel,
 * custom raw <head> HTML) into document.head — admins paste the IDs in
 * Admin → Settings and they go live on next reload (no rebuild required).
 */
export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    // Defer to idle so /api/settings doesn't compete with the LCP image fetch.
    // The site renders fine without settings (defaults kick in via context fallback);
    // analytics/pixel scripts only need to attach BEFORE users interact, not before
    // the first paint. This shaves ~150-300ms off mobile FCP/TBT.
    const fetchSettings = () => {
      api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => setSettings({}));
    };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = window.requestIdleCallback(fetchSettings, { timeout: 2000 });
      return () => { try { window.cancelIdleCallback(id); } catch { /* noop */ } };
    }
    const t = setTimeout(fetchSettings, 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!settings) return;
    const injected = [];

    const addEl = (tag, attrs, html) => {
      const el = document.createElement(tag);
      Object.entries(attrs || {}).forEach(([k, v]) => { if (v != null) el.setAttribute(k, v); });
      if (html) el.innerHTML = html;
      el.setAttribute("data-injected-by", "site-settings");
      document.head.appendChild(el);
      injected.push(el);
    };

    // Google Search Console verification meta
    if (settings.google_search_console_verification) {
      addEl("meta", { name: "google-site-verification", content: settings.google_search_console_verification });
    }

    // Google Analytics 4
    if (settings.google_analytics_id) {
      addEl("script", { src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(settings.google_analytics_id)}`, async: "" });
      addEl("script", {}, `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${settings.google_analytics_id}');`);
    }

    // Google Tag Manager
    if (settings.google_tag_manager_id) {
      addEl("script", {}, `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${settings.google_tag_manager_id}');`);
    }

    // Facebook / Meta Pixel
    if (settings.facebook_pixel_id) {
      addEl("script", {}, `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${settings.facebook_pixel_id}');fbq('track','PageView');`);
    }

    // Custom raw HTML — power-user injection
    if (settings.custom_head_html) {
      const range = document.createRange();
      range.selectNode(document.head);
      const frag = range.createContextualFragment(settings.custom_head_html);
      Array.from(frag.childNodes).forEach((node) => {
        if (node.setAttribute) node.setAttribute("data-injected-by", "site-settings-custom");
        document.head.appendChild(node);
        injected.push(node);
      });
    }

    // Brand favicon — replaces the default <link rel="icon"> dynamically
    if (settings.brand_favicon) {
      // Remove any existing favicon links so the admin upload takes precedence
      document.head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach((el) => {
        if (!el.getAttribute("data-injected-by")) el.setAttribute("data-original-href", el.getAttribute("href") || "");
        el.remove();
      });
      addEl("link", { rel: "icon", type: "image/png", href: settings.brand_favicon });
      addEl("link", { rel: "apple-touch-icon", href: settings.brand_favicon });
    }

    return () => {
      injected.forEach((el) => { try { el.remove(); } catch { /* noop */ } });
    };
  }, [settings]);

  return (
    <SiteSettingsContext.Provider value={settings || {}}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext) || {};
}

/**
 * Updates document.title and the standard SEO meta tags (description,
 * keywords, OG, Twitter) for the current page. Falls back through
 * page-specific → site-wide → hard-coded defaults.
 */
/**
 * useSeoTags
 *
 * Imperatively injects <title>, meta description, keywords, Open Graph and
 * Twitter Cards into <head> for the current page. Falls back through
 * page-specific → site-wide → hard-coded defaults.
 *
 * Optional params:
 *   - canonical: full https://... URL for <link rel="canonical">
 *   - schemas:   array of JSON-LD schema objects rendered as inline
 *                <script type="application/ld+json"> tags. Cleaned up on
 *                unmount so the next page doesn't inherit stale schemas.
 *   - noindex:   if true, sets robots=noindex,nofollow (use for admin pages).
 */
export function useSeoTags({ title, description, keywords, image, url, canonical, schemas, noindex } = {}) {
  const site = useSiteSettings();
  const schemasKey = schemas ? JSON.stringify(schemas) : "";
  const [override, setOverride] = useState(null);

  // Look up an admin-managed override for the current path. Once fetched, the
  // override's fields take precedence over the page's hard-coded SEO inputs.
  // Wildcard matches (e.g. `/p/*`) are handled server-side.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = window.location.pathname;
    let cancelled = false;
    api.get("/seo/override", { params: { path } })
      .then((r) => { if (!cancelled) setOverride(r.data || null); })
      .catch(() => { if (!cancelled) setOverride(null); });
    return () => { cancelled = true; };
  }, [typeof window !== "undefined" ? window.location.pathname : ""]);

  useEffect(() => {
    const finalTitle = (override?.title) || title || site.seo_title || "XYZPLUS";
    const finalDesc = (override?.description) || description || site.seo_description || "";
    const finalKw = (override?.keywords) || keywords || site.seo_keywords || "";
    const FALLBACK_OG_IMAGE = "https://customer-assets.emergentagent.com/job_custom-catalog-6/artifacts/xzqwlruk_xyzplus%20logo.png";
    const finalImg = (override?.og_image) || image || site.seo_og_image || FALLBACK_OG_IMAGE;
    const finalUrl = url || canonical || (typeof window !== "undefined" ? window.location.href : "");
    const finalNoindex = (override?.noindex !== undefined ? !!override.noindex : !!noindex);
    document.title = finalTitle;
    const set = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };
    set('meta[name="description"]', { name: "description", content: finalDesc });
    if (finalKw) set('meta[name="keywords"]', { name: "keywords", content: finalKw });
    set('meta[property="og:title"]', { property: "og:title", content: finalTitle });
    set('meta[property="og:description"]', { property: "og:description", content: finalDesc });
    set('meta[property="og:type"]', { property: "og:type", content: "website" });
    set('meta[property="og:site_name"]', { property: "og:site_name", content: "XYZPLUS" });
    if (finalImg) set('meta[property="og:image"]', { property: "og:image", content: finalImg });
    if (finalUrl) set('meta[property="og:url"]', { property: "og:url", content: finalUrl });
    set('meta[name="twitter:card"]', { name: "twitter:card", content: finalImg ? "summary_large_image" : "summary" });
    set('meta[name="twitter:title"]', { name: "twitter:title", content: finalTitle });
    set('meta[name="twitter:description"]', { name: "twitter:description", content: finalDesc });
    if (finalImg) set('meta[name="twitter:image"]', { name: "twitter:image", content: finalImg });
    set('meta[name="robots"]', { name: "robots", content: finalNoindex ? "noindex,nofollow" : "index,follow,max-image-preview:large" });

    // Canonical link
    let canonicalRef = null;
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      const created = !link;
      const prevHref = link ? link.getAttribute("href") : null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      canonicalRef = { el: link, created, prevHref };
    }

    // JSON-LD schema scripts (cleaned up on unmount)
    const schemaEls = (Array.isArray(schemas) ? schemas : []).filter(Boolean).map((s, i) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.seoSchema = `useSeoTags-${i}`;
      try { el.textContent = JSON.stringify(s); } catch (_e) { el.textContent = "{}"; }
      document.head.appendChild(el);
      return el;
    });

    return () => {
      if (canonicalRef) {
        if (canonicalRef.created) canonicalRef.el.remove();
        else if (canonicalRef.prevHref) canonicalRef.el.setAttribute("href", canonicalRef.prevHref);
      }
      schemaEls.forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, image, url, canonical, noindex, schemasKey, override, site.seo_title, site.seo_description, site.seo_keywords, site.seo_og_image]);

  // Expose the override-supplied H1 so pages can opt-in to admin overrides.
  return { overrideH1: override?.h1 || null };
}
